import { useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import ListSubheader from '@mui/material/ListSubheader'
import { useTheme } from '@mui/material/styles'

import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'

import { AppContext } from "/providers/app-context"
import logo from '/assets/images/smartix-logo.png'

export default function ButtonAppBar({ title, toggleHelp, toggleSettings, hideSettings = false }) {

  const navigate = useNavigate()
  const theme = useTheme()

  const { client, creditBalance, selectedClientId, selectClient } = useContext(AppContext)
  const availableClients = client?.available_clients ?? []
  const parentClientId = client?.client_id?.toString()
  const parentClient = availableClients.find(item => item.client_id?.toString() === parentClientId)
  const childClients = availableClients.filter(item => item.client_id?.toString() !== parentClientId)
  const selectedClient = availableClients.find(item => item.client_id?.toString() === selectedClientId?.toString())
  const showClientSelector = availableClients.length > 1
  const showCreditBalance = client?.billing_mode === "prepay" && creditBalance
  const totalAvailableCredits = Number(creditBalance?.available_credits ?? ((creditBalance?.free_credits ?? 0) + (creditBalance?.credits ?? 0)))
  const overdraftLimit = Number(creditBalance?.overdraft ?? 0)
  const totalCredits = Number(creditBalance?.free_credits ?? 0) + Number(creditBalance?.credits ?? 0)
  const availableWithOverdraft = Number(creditBalance?.available_with_overdraft ?? (totalCredits + overdraftLimit))
  const overdraftRemaining = Math.max(0, availableWithOverdraft - totalCredits)
  const overdraftUsed = Math.max(0, overdraftLimit - overdraftRemaining)

  useEffect(() => {
    document.title = "Smartix: " + title
  }, [title])


  return (
    <Box >
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: "menubar.bg", color: "menubar.fg" }}>
        <Toolbar>
          <img src={logo} style={{ height: 50, marginRight: 10 }} />

          <Typography variant="h6" component="div" sx={{ flexGrow: 1, flexShrink: 0, whitespace: "nowrap" }}>
            <span className='smartix'>SMARTIX</span> <span className='studio'>Studio</span> : {title ?? ""}
          </Typography>

          {showCreditBalance ? (
            <Button
              onClick={() => navigate("/billing/")}
              variant="outlined"
              sx={{ mr: 5 }}
            >
              {overdraftUsed > 0
                ? `CREDITS OVERDRAWN ${overdraftUsed.toLocaleString()} of ${overdraftLimit.toLocaleString()}`
                : `Credit balance: ${totalAvailableCredits.toLocaleString()}`}
            </Button>
          ) : null}

          {showClientSelector ? (
            <Select
              size='small'
              value={selectedClientId?.toString() ?? ''}
              onChange={e => selectClient(e.target.value)}
              renderValue={() => {
                if (!selectedClient) return ''
                const scopeLabel = selectedClient.client_id?.toString() === parentClientId ? 'Parent' : 'Child'
                return `${scopeLabel}: ${selectedClient.name || "Unnamed client"}`
              }}
              sx={{
                mr: 2,
                minWidth: 320,
                color: 'inherit',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'currentColor' },
                '& .MuiSvgIcon-root': { color: 'inherit' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme.palette.mode === 'dark' ? '#303030' : '#ffffff',
                    color: 'text.primary',
                    '& .MuiListSubheader-root': {
                      backgroundColor: 'transparent',
                      color: 'text.primary',
                      fontSize: '1rem',
                      fontWeight: 700,
                      lineHeight: 2.5,
                    },
                    '& .MuiMenuItem-root': {
                      fontSize: '0.95rem',
                    },
                  },
                },
              }}
            >
              {parentClient ? (
                <MenuItem key={parentClient.client_id} value={parentClient.client_id.toString()}>
                  <span className="font-bold">Parent client: </span> {parentClient.name || "Unnamed client"}
                </MenuItem>
              ) : null}

              {childClients.length ?
                <div className='-ml-3'>
                  <ListSubheader>Child clients</ListSubheader>
                </div>
                : null}

              {childClients.map(item => (
                <MenuItem key={item.client_id} value={item.client_id.toString()}>
                  <div  className="ml-3">{item.name || "Unnamed client"}</div>
                </MenuItem>
              ))}

            </Select>
          ) : null}

          {!hideSettings ? <IconButton onClick={toggleSettings} color='inherit'><SettingsOutlinedIcon /></IconButton> : null}
          <IconButton onClick={toggleHelp} color='inherit'><HelpOutlineIcon /></IconButton>
        </Toolbar>
      </AppBar>
    </Box>
  )
}
